import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  IconButton,
  Input,
  Avatar,
  Badge,
  Divider,
  Spinner,
  useColorModeValue,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  Link,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spacer,
} from "@chakra-ui/react";
import {
  FiSend,
  FiPaperclip,
  FiMoreVertical,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiMessageSquare,
  FiImage,
  FiFileText,
  FiVideo,
  FiMic,
  FiUsers,
  FiInfo,
  FiDownload,
  FiX,
  FiMaximize2,
  FiFilter,
  FiShield,
  FiCopy,
  FiExternalLink,
  FiChevronDown,
  FiChevronsDown,
  FiCheckCircle,
  FiLayers,
} from "react-icons/fi";

import {
  ref as dbRef,
  push,
  onChildAdded,
  off,
  set,
  get,
  onChildRemoved,
  onValue,
  query as rtdbQuery,
  orderByKey,
  limitToLast,
  startAt,
  endBefore,
  limitToFirst,
} from "firebase/database";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  onSnapshot,
  setDoc,
  increment,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  getBlob,
  updateMetadata,
} from "firebase/storage";
import { rtdb, db, storage, auth } from "../../firebase/firebase";
import { format, isSameDay, isYesterday } from "date-fns";
import { ChakraIcon } from "../../components/ChakraIcon";
import { showToast } from "../../utils/toastUtils";
import { ResolvedImage, ResolvedVideo, ResolvedAudio } from "../../components/ResolvedMedia";
import { resolveRole } from "../../utils/permissions";
import {
  MessageMeta,
  ChatRowTicks,
  deriveTickState,
  useReceipts,
  usePublishReceipts,
  useMessageOutbox,
} from "../../components/chat/messageStatus";
import {
  MentionPicker,
  MENTION_ALL_IDS,
  applyMention,
  collectMentions,
  filterMentionCandidates,
  findMentionQuery,
  mentionsUser,
  splitMentions,
  type MentionCandidate,
  type MentionQuery,
  type MentionTarget,
} from "../../components/chat/mentions";


interface Message {
  id: string;
  text?: string;
  userId: string;
  createdAt: number;
  type: string;
  fileUrl?: string;
  url?: string;
  fileName?: string;
  locationName?: string;
  caption?: string;
  taskName?: string;
  taskId?: string;
  subTaskId?: string;
  mentions?: MentionTarget[];
}

interface Channel {
  id: string;
  name: string;
  lastMessage?: {
    text: string;
    time: number;
    userId: string;
    type: string;
  };
  unreadCount?: number;
}

interface Project {
  id: string;
  name: string;
  channels: Channel[];
}

interface TeamChatsMainProps {
  currentUser: any;
  organizationId: string;
  projects: any[];
  users: any[];
  orgAdminId?: string;
}

// Admin = permission "Admin", role "admin", or the organization's admin (org admin).
const isAdminMember = (u: any, orgAdminId?: string): boolean =>
  resolveRole(u).isAdmin || (!!orgAdminId && u?.id === orgAdminId);

const TeamChatsMain: React.FC<TeamChatsMainProps> = ({
  currentUser,
  organizationId,
  projects: initialProjects,
  users,
  orgAdminId,
}) => {
  const [selectedChannel, setSelectedChannel] = useState<{
    projectId: string;
    channel: Channel;
  } | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(true);
  const [isMessageSearchOpen, setIsMessageSearchOpen] = useState(false);
  const [messageSearchText, setMessageSearchText] = useState("");
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
  const [viewerMedia, setViewerMedia] = useState<{ url: string; type: string; name: string } | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [firstMessageKey, setFirstMessageKey] = useState<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // Task & Task Group state
  const [isTaskGroupModalOpen, setIsTaskGroupModalOpen] = useState(false);
  const [isTaskSelectModalOpen, setIsTaskSelectModalOpen] = useState(false);
  const [isSubTaskModalOpen, setIsSubTaskModalOpen] = useState(false);
  const [taskGroupList, setTaskGroupList] = useState<any[]>([]);
  const [selectedTaskGroup, setSelectedTaskGroup] = useState<any>(null);
  const [channelMembers, setChannelMembers] = useState<any[]>([]);

  // Task Form state
  const [taskGroupTitle, setTaskGroupTitle] = useState("");
  const [taskGroupDesc, setTaskGroupDesc] = useState("");
  const [subTaskName, setSubTaskName] = useState("");
  const [plannedStart, setPlannedStart] = useState("");
  const [plannedEnd, setPlannedEnd] = useState("");
  const [assignedSubTaskUsers, setAssignedSubTaskUsers] = useState<string[]>([]);
  const [isSavingTask, setIsSavingTask] = useState(false);

  const userPermissions = currentUser?.permissions;
  const userRole = currentUser?.role?.toLowerCase();

  const hasTaskPermission = userPermissions?.toLowerCase() === "admin" ||
    userPermissions?.toLowerCase() === "editor" ||
    userPermissions?.toLowerCase() === "viewer" ||
    (!userPermissions && (userRole === "admin" || userRole === "manager" || userRole === "managing director"));

  const scrollRef = useRef<HTMLDivElement>(null);
  const initialScrollDoneRef = useRef(false);
  const messageInputRef = useRef<HTMLInputElement>(null);

  /* ---------------------------------------------------------------------- */
  /*  @MENTIONS                                                              */
  /* ---------------------------------------------------------------------- */
  const [mentionQuery, setMentionQuery] = useState<MentionQuery | null>(null);

  const mentionCandidates = useMemo<MentionCandidate[]>(
    () => [
      { id: MENTION_ALL_IDS[0], name: "All Users", isAll: true },
      ...channelMembers
        .filter((m) => m.id && m.id !== currentUser?.id)
        .map((m) => ({ id: m.id, name: m.name })),
    ],
    [channelMembers, currentUser?.id]
  );

  const mentionMatches = useMemo(
    () => (mentionQuery ? filterMentionCandidates(mentionCandidates, mentionQuery.term) : []),
    [mentionQuery, mentionCandidates]
  );

  const handleMessageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setInputText(next);
    setMentionQuery(findMentionQuery(next, e.target.selectionStart ?? next.length));
  };

  // Arrow keys / clicks move the caret without firing onChange, so the query
  // needs to be re-derived from wherever the caret actually lands.
  const handleMessageInputCaretMove = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    setMentionQuery(findMentionQuery(target.value, target.selectionStart ?? target.value.length));
  };

  const handlePickMention = (c: MentionCandidate) => {
    setMentionQuery((q) => {
      if (!q) return null;
      const next = applyMention(inputText, q, c);
      setInputText(next.text);
      // Caret restore has to wait for the value to actually land in the DOM.
      requestAnimationFrame(() => {
        messageInputRef.current?.focus();
        messageInputRef.current?.setSelectionRange(next.caret, next.caret);
      });
      return null;
    });
  };


  const cardBg = useColorModeValue("white", "#1A1C1F");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const sidebarBg = useColorModeValue("gray.50", "#14161B");
  const activeChatBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.500", "gray.400");
  const chatBg = useColorModeValue("#F0F2F5", "#0B0B0F");
  const searchInputBg = useColorModeValue("white", "gray.800");
  const messageMeBg = useColorModeValue("gray.100", "gray.700"); // Light gray for me
  const messageOtherBg = useColorModeValue("white", "gray.800");
  const messageMeTextColor = useColorModeValue("gray.800", "white");
  const dateBadgeBg = useColorModeValue("white", "gray.700");
  const inputBg = useColorModeValue("gray.100", "gray.800");
  const inputHoverBg = useColorModeValue("gray.200", "gray.700");
  const inputFocusBg = useColorModeValue("white", "gray.700");
  const scrollBtnBg = useColorModeValue("gray.100", "gray.700");
  const scrollBtnColor = useColorModeValue("gray.600", "gray.200");
  const scrollBtnHoverBg = useColorModeValue("gray.200", "gray.600");

  const updateChannelLast = async (lastMsg: { type: string, text: string }) => {
    if (!organizationId || !selectedChannel || !currentUser) return;
    const { projectId, channel } = selectedChannel;
    const channelRef = doc(db, `organization/${organizationId}/projects/${projectId}/channels`, channel.id);
    await updateDoc(channelRef, {
      lastMessage: {
        text: lastMsg.text,
        time: Date.now(),
        type: lastMsg.type,
        userId: currentUser.id,
      }
    });
  };

  const bumpChannelUnread = async (projectId: string, channelId: string) => {
    if (!organizationId || !currentUser) return;
    try {
      const updates: Record<string, any> = {};
      for (const m of users) {
        if (!m?.id || m.id === currentUser.id) continue;
        const role = (m.role || "").toLowerCase().replace(/\s+/g, "");
        const isAdmin = isAdminMember(m, orgAdminId) || role === "manager" || role === "managingdirector";
        const assigned = m.assignedChannels?.[projectId] || [];
        if (isAdmin || assigned.includes(channelId)) {
          updates[`userUnreadCounts.${m.id}`] = increment(1);
        }
      }
      if (Object.keys(updates).length > 0) {
        const channelRef = doc(db, `organization/${organizationId}/projects/${projectId}/channels`, channelId);
        await updateDoc(channelRef, updates);
      }
    } catch (err) {
      console.error("Failed to increment channel unread counts", err);
    }
  };

  // Load projects and channels with real-time unread counts
  useEffect(() => {
    if (!organizationId || !currentUser) return;

    const fetchProjectsAndChannels = async () => {
      setIsLoadingChannels(true);
      try {
        const projectsWithChannels: Project[] = [];
        // Admins (permission, role or org admin) see every project and every channel
        const isAdmin = isAdminMember(currentUser, orgAdminId);

        for (const proj of initialProjects) {
          // Skip if not assigned and not admin
          if (!isAdmin && !currentUser.assignedProjects?.includes(proj.id)) continue;

          const channelsCol = collection(db, `organization/${organizationId}/projects/${proj.id}/channels`);
          const channelsSnap = await getDocs(channelsCol);

          const channels: Channel[] = channelsSnap.docs
            .filter(doc => isAdmin || currentUser.assignedChannels?.[proj.id]?.includes(doc.id))
            .map(doc => {
              const data = doc.data();
              const unreadCount = data.userUnreadCounts?.[currentUser.id] || 0;
              return {
                id: doc.id,
                name: data.name || "General",
                lastMessage: data.lastMessage,
                unreadCount
              };
            });

          if (channels.length > 0) {
            projectsWithChannels.push({
              id: proj.id,
              name: proj.name,
              channels
            });
          }
        }
        setProjectList(projectsWithChannels);
      } catch (err) {
        console.error("Error loading channels:", err);
      } finally {
        setIsLoadingChannels(false);
      }
    };

    fetchProjectsAndChannels();

    // Set up snapshot listeners for each channel to update unread counts and last messages
    const unsubscribes: (() => void)[] = [];
    initialProjects.forEach(proj => {
      const channelsCol = collection(db, `organization/${organizationId}/projects/${proj.id}/channels`);
      const unsub = onSnapshot(channelsCol, (snap) => {
        setProjectList(prev => prev.map(p => {
          if (p.id === proj.id) {
            const updatedChannels = p.channels.map(ch => {
              const doc = snap.docs.find(d => d.id === ch.id);
              if (doc) {
                const data = doc.data();
                return {
                  ...ch,
                  lastMessage: data.lastMessage,
                  unreadCount: data.userUnreadCounts?.[currentUser.id] || 0
                };
              }
              return ch;
            });
            return { ...p, channels: updatedChannels };
          }
          return p;
        }));
      });
      unsubscribes.push(unsub);
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, [organizationId, currentUser, initialProjects, orgAdminId]);

  /* ---------------------------------------------------------------------- */
  /*  DELIVERY STATUS + READ RECEIPTS + RETRYABLE SENDS                      */
  /* ---------------------------------------------------------------------- */
  const channelBasePath = useMemo(
    () =>
      organizationId && selectedChannel
        ? `organization/${organizationId}/projects/${selectedChannel.projectId}/channels/${selectedChannel.channel.id}`
        : null,
    [organizationId, selectedChannel]
  );
  const messagesPath = channelBasePath ? `${channelBasePath}/messages` : null;

  // Retryable sends for every message this screen writes into this channel.
  const outbox = useMessageOutbox(messagesPath);

  const receipts = useReceipts(channelBasePath);
  const newestTs = useMemo(
    () => messages.reduce((t, m) => (m.createdAt > t ? m.createdAt : t), 0),
    [messages]
  );
  usePublishReceipts(channelBasePath, currentUser?.id, newestTs, true);

  // Everyone in the channel except me. A channel has many readers, so "read"
  // is not a single person's act — this module deliberately stops at two
  // grey ticks and leaves the blue one to direct chats. Taken from the
  // receipts themselves (anyone who has written one has by definition opened
  // this channel) rather than the member roster, which loads separately.
  const peerUids = useMemo(
    () => Object.keys(receipts).filter((id) => id && id !== currentUser?.id),
    [receipts, currentUser?.id]
  );
  const tickStateFor = useCallback(
    (m: Message) =>
      deriveTickState({
        sendState: outbox.entries[m.id]?.state,
        createdAt: m.createdAt,
        receipts,
        peerUids,
        trackRead: false,
      }),
    [outbox.entries, receipts, peerUids]
  );

  // An unacknowledged send is normally already in `messages` — the Realtime
  // Database echoes a local write before the server sees it. It is only
  // missing when the write was rejected and rolled back, which is exactly
  // when the user needs it kept on screen with a retry affordance.
  const displayMessages = useMemo(() => {
    const known = new Set(messages.map((m) => m.id));
    const stranded = Object.values(outbox.entries)
      .filter((e) => !known.has(e.id))
      .map((e) => ({ id: e.id, ...e.msg } as Message));
    return [...messages, ...stranded].sort((a, b) => a.createdAt - b.createdAt);
  }, [messages, outbox.entries]);

  // Scroll to bottom helper
  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior
      });
      setIsAtBottom(true);
      setShowScrollButton(false);
    }
  };

  // Handle manual scroll to detect when to show the bottom button
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
    setIsAtBottom(isBottom);
    setShowScrollButton(!isBottom);
  };

  // Load messages when a channel is selected
  useEffect(() => {
    if (!selectedChannel || !organizationId || !currentUser?.id) return;

    setIsLoadingMessages(true);
    setMessages([]);
    setHasMoreMessages(true);
    setFirstMessageKey(null);

    const path = `organization/${organizationId}/projects/${selectedChannel.projectId}/channels/${selectedChannel.channel.id}/messages`;
    const messagesRef = dbRef(rtdb, path);
    const messagesQuery = rtdbQuery(messagesRef, orderByKey(), limitToLast(25));

    const handleNewMessage = (snapshot: any) => {
      const msg = snapshot.val();
      const newMessage = { id: snapshot.key, ...msg };

      setMessages(prev => {
        const exists = prev.some(m => m.id === newMessage.id);
        if (exists) return prev;
        const updated = [...prev, newMessage];
        return updated.sort((a, b) => a.createdAt - b.createdAt);
      });

      // Auto-scroll logic if already at bottom or it's my message
      if (initialScrollDoneRef.current && (isAtBottom || newMessage.userId === currentUser.id)) {
        setTimeout(() => scrollToBottom("smooth"), 100);
      }
    };

    const handleRemovedMessage = (snapshot: any) => {
      setMessages(prev => prev.filter(m => m.id !== snapshot.key));
    };

    onChildAdded(dbRef(rtdb, path), (snapshot) => {
      if (!firstMessageKey) setFirstMessageKey(snapshot.key);
    });

    onChildAdded(messagesQuery, handleNewMessage);
    onChildRemoved(messagesRef, handleRemovedMessage);

    // Initial check to resolve loading state if no messages exist
    onValue(messagesQuery, (snapshot) => {
      setIsLoadingMessages(false);
      if (snapshot.exists()) {
        const keys = Object.keys(snapshot.val());
        if (keys.length > 0) {
          setFirstMessageKey(keys[0]);
          setHasMoreMessages(keys.length === 25);
        }
      }
    }, { onlyOnce: true });

    // Mark as read
    const markAsRead = async () => {
      const channelRef = doc(db, `organization/${organizationId}/projects/${selectedChannel.projectId}/channels`, selectedChannel.channel.id);
      await updateDoc(channelRef, {
        [`userUnreadCounts.${currentUser.id}`]: 0
      });
    };
    markAsRead();

    return () => {
      off(messagesQuery, "child_added", handleNewMessage);
      off(messagesRef, "child_removed", handleRemovedMessage);
    };
  }, [selectedChannel, organizationId, currentUser?.id]);

  // Reset scroll completion state when channel changes
  useEffect(() => {
    initialScrollDoneRef.current = false;
  }, [selectedChannel?.channel.id]);

  // Scroll to bottom once initial messages are loaded
  useEffect(() => {
    if (!isLoadingMessages && messages.length > 0 && !initialScrollDoneRef.current) {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
      initialScrollDoneRef.current = true;
    }
  }, [messages, isLoadingMessages]);

  const handleDownload = async (url: string, fileName: string) => {
    try {
      const storageReference = storageRef(storage, url);

      // 1. Force attachment metadata
      await updateMetadata(storageReference, {
        contentDisposition: `attachment; filename="${fileName}"`,
      });

      // 2. Refresh URL with a cache-buster to bypass browser caching of the "view" header
      const baseDirectUrl = await getDownloadURL(storageReference);
      const directUrl = `${baseDirectUrl}&t=${Date.now()}`;

      // 3. Use an iframe to trigger the download WITHOUT navigating or opening a tab
      // This is the most reliable way to trigger "Save As" for cross-origin resources
      let iframe = document.getElementById('hidden-download-frame') as HTMLIFrameElement;
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'hidden-download-frame';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
      }
      iframe.src = directUrl;
    } catch (error: any) {
      console.error("Download failed:", error);
      showToast({
        title: "Opening in viewer...",
        description: "Native save was blocked. Please right-click the file in the new tab and select 'Save As'.",
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
      window.open(url, '_blank');
    }
  };

  const loadMoreMessages = useCallback(async () => {
    if (!firstMessageKey || !selectedChannel || !organizationId) return;

    const { projectId, channel } = selectedChannel;
    const path = `organization/${organizationId}/projects/${projectId}/channels/${channel.id}/messages`;
    const messagesRef = dbRef(rtdb, path);
    // Query messages ending before the current first message
    const q = rtdbQuery(messagesRef, orderByKey(), endBefore(firstMessageKey), limitToLast(30));

    const container = scrollRef.current;
    const prevScrollHeight = container ? container.scrollHeight : 0;
    const prevScrollTop = container ? container.scrollTop : 0;

    const snapshot = await get(q);
    if (snapshot.exists()) {
      const val = snapshot.val();
      const newMessages = Object.keys(val).map(key => ({ id: key, ...val[key] }));
      setMessages(prev => [...newMessages, ...prev].sort((a, b) => a.createdAt - b.createdAt));
      const keys = Object.keys(val);
      setFirstMessageKey(keys[0]);
      setHasMoreMessages(keys.length === 30);

      // Pin the scroll position so it doesn't jump
      if (container) {
        setTimeout(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        }, 50);
      }
    } else {
      setHasMoreMessages(false);
    }
  }, [selectedChannel, organizationId, firstMessageKey]);

  const sendMessage = async () => {
    if (!inputText.trim() || !selectedChannel || !currentUser) return;

    const { projectId, channel } = selectedChannel;
    const trimmedText = inputText.trim();
    // Resolved against the roster the picker offered, so a name typed by
    // hand still becomes a real mention as long as it matches a member.
    const mentions = collectMentions(trimmedText, mentionCandidates);
    setInputText("");
    setMentionQuery(null);

    const syncMetadata = async () => {
      try {
        // Update last message in Firestore
        const channelRef = doc(db, `organization/${organizationId}/projects/${projectId}/channels`, channel.id);
        await updateDoc(channelRef, {
          lastMessage: {
            text: trimmedText,
            time: Date.now(),
            type: "text",
            userId: currentUser.id,
          }
        });

        void bumpChannelUnread(projectId, channel.id);
      } catch (error) {
        console.error("Error updating channel metadata:", error);
      }
    };

    // Retryable: outbox keeps the message visible with a "sending" clock and
    // flips it to a tap-to-retry state if the write times out or is rejected,
    // instead of silently failing on a dead/slow connection.
    await outbox.send(
      () => {
        const newMessage: any = {
          text: trimmedText,
          createdAt: Date.now(),
          userId: currentUser.id,
          type: "text",
          projectId,
          channelId: channel.id,
          organizationId,
        };
        if (mentions.length) newMessage.mentions = mentions;
        return newMessage;
      },
      syncMetadata
    );
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = event.target.files?.[0];
    if (!file || !selectedChannel || !currentUser) return;

    setIsUploading(true);
    const { projectId, channel } = selectedChannel;
    const fileName = `${Date.now()}_${file.name}`;
    const sRef = storageRef(storage, `uploads/${channel.id}/${fileName}`);
    const uploadTask = uploadBytesResumable(sRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Upload error:", error);
        setIsUploading(false);
        showToast({ title: "Upload Failed", status: "error" });
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

        // The upload already succeeded; only the message node is left, so a
        // failure here is retryable without re-uploading the file.
        await outbox.send(
          () => ({
            createdAt: Date.now(),
            userId: currentUser.id,
            type: type === "image" ? "image" : type === "video" ? "video" : "document",
            fileUrl: downloadURL,
            fileName: file.name,
            projectId,
            channelId: channel.id,
            organizationId,
          }),
          async () => {
            try {
              const channelRef = doc(db, `organization/${organizationId}/projects/${projectId}/channels`, channel.id);
              await updateDoc(channelRef, {
                lastMessage: {
                  text: type.charAt(0).toUpperCase() + type.slice(1),
                  time: Date.now(),
                  type,
                  userId: currentUser.id,
                }
              });

              void bumpChannelUnread(projectId, channel.id);
            } catch (error) {
              console.error("Error updating channel metadata:", error);
            }
          }
        );

        setIsUploading(false);
        setUploadProgress(0);
      }
    );
  };

  const fetchTaskGroups = useCallback(async () => {
    if (!organizationId || !selectedChannel) return;
    const { projectId, channel } = selectedChannel;
    const tasksCol = collection(db, `organization/${organizationId}/projects/${projectId}/channels/${channel.id}/tasks`);
    const snap = await getDocs(tasksCol);
    const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTaskGroupList(list);
  }, [organizationId, selectedChannel]);

  const fetchChannelMembers = useCallback(async () => {
    if (!organizationId || !selectedChannel) return;
    const { projectId, channel } = selectedChannel;
    try {
      const chanRef = doc(db, `organization/${organizationId}/projects/${projectId}/channels/${channel.id}`);
      const chanSnap = await getDoc(chanRef);
      if (chanSnap.exists()) {
        const data = chanSnap.data();
        const assignedUserIds = data.assignedUsers || [];
        const orgSnap = await getDoc(doc(db, `organization/${organizationId}`));
        const orgMembers = orgSnap.exists() ? (orgSnap.data().members || []) : [];
        const members = orgMembers.filter((m: any) => assignedUserIds.includes(m.id) || isAdminMember(m, orgAdminId));
        setChannelMembers(members.map((m: any) => ({
          id: m.id,
          name: `${m.firstName} ${m.lastName}`,
          role: m.role || ""
        })));
      }
    } catch (e) {
      console.error("Error fetching members", e);
    }
  }, [organizationId, selectedChannel, orgAdminId]);

  // The task flows already fetched this lazily; mentions need the roster as
  // soon as a channel opens, not just when someone opens the task modal.
  useEffect(() => {
    if (selectedChannel) void fetchChannelMembers();
    else setChannelMembers([]);
  }, [selectedChannel, fetchChannelMembers]);

  const handleCreateTaskGroup = async () => {
    if (!taskGroupTitle.trim() || !selectedChannel || !currentUser) return;
    setIsSavingTask(true);
    const { projectId, channel } = selectedChannel;
    try {
      const tasksCol = collection(db, `organization/${organizationId}/projects/${projectId}/channels/${channel.id}/tasks`);
      const newTaskRef = doc(tasksCol);
      const taskId = newTaskRef.id;

      await set(dbRef(rtdb, `organization/${organizationId}/projects/${projectId}/channels/${channel.id}/messages/${push(dbRef(rtdb)).key}`), {
        type: "task",
        taskName: taskGroupTitle.trim(),
        taskId: taskId,
        createdAt: Date.now(),
        userId: currentUser.id,
      });

      await setDoc(newTaskRef, {
        title: taskGroupTitle.trim(),
        description: taskGroupDesc.trim(),
        createdAt: Date.now(),
        data: []
      });

      setTaskGroupTitle("");
      setTaskGroupDesc("");
      setIsTaskGroupModalOpen(false);
      await updateChannelLast({ type: 'task', text: `Task Group: ${taskGroupTitle.trim()}` });
      void bumpChannelUnread(projectId, channel.id);
      showToast({ title: "Task Group Created", status: "success" });
    } catch (e) {
      console.error(e);
      showToast({ title: "Error creating Task Group", status: "error" });
    } finally {
      setIsSavingTask(false);
    }
  };

  const handleCreateSubTask = async () => {
    if (!subTaskName.trim() || !selectedTaskGroup || !selectedChannel || !currentUser) return;
    setIsSavingTask(true);
    const { projectId, channel } = selectedChannel;
    try {
      const taskDocRef = doc(db, `organization/${organizationId}/projects/${projectId}/channels/${channel.id}/tasks`, selectedTaskGroup.id);
      const subTaskId = Math.floor(Math.random() * 1000000000).toString();

      const newSubTask = {
        id: subTaskId,
        name: subTaskName.trim(),
        plannedStart,
        plannedEnd,
        assignedUsers: assignedSubTaskUsers,
        status: "Not-started",
        wbs: (selectedTaskGroup.data?.length || 0) + 1
      };

      const updatedData = [...(selectedTaskGroup.data || []), newSubTask];
      await updateDoc(taskDocRef, { data: updatedData });

      await set(dbRef(rtdb, `organization/${organizationId}/projects/${projectId}/channels/${channel.id}/messages/${push(dbRef(rtdb)).key}`), {
        type: "task",
        taskName: subTaskName.trim(),
        taskId: selectedTaskGroup.id,
        subTaskId: subTaskId,
        createdAt: Date.now(),
        userId: currentUser.id,
      });

      setSubTaskName("");
      setPlannedStart("");
      setPlannedEnd("");
      setAssignedSubTaskUsers([]);
      setIsSubTaskModalOpen(false);
      await updateChannelLast({ type: 'task', text: `Task: ${subTaskName.trim()}` });
      void bumpChannelUnread(projectId, channel.id);
      showToast({ title: "Task Created", status: "success" });
    } catch (e) {
      console.error(e);
      showToast({ title: "Error creating Task", status: "error" });
    } finally {
      setIsSavingTask(false);
    }
  };

  const formatLastMessageTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();

    if (isSameDay(date, now)) {
      return date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toLowerCase();
    }

    if (isYesterday(date)) {
      return "Yesterday";
    }

    return date.toLocaleDateString('en-GB'); // dd/mm/yyyy
  };

  // `mentions` is the list carried on the message, so only names the sender
  // actually resolved get highlighted — a "@someone" typed by hand stays
  // plain text, and an old message keeps rendering correctly after a rename.
  const renderMessageText = (text: string, mentions?: MentionTarget[]) => {
    if (!text) return null;

    // Regex to find URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <Link
            key={i}
            href={part}
            isExternal
            color="blue.500"
            textDecoration="underline"
            _hover={{ color: "blue.600" }}
          >
            {part}
          </Link>
        );
      }
      return splitMentions(part, mentions).map((seg, segIndex) => {
        if (!seg.mention) return <React.Fragment key={`${i}-${segIndex}`}>{seg.text}</React.Fragment>;
        const aboutMe = mentionsUser([seg.mention], currentUser.id);
        return (
          <Text
            as="span"
            key={`${i}-${segIndex}`}
            fontWeight="700"
            color="#8A6A1F"
            bg={aboutMe ? "rgba(251,210,130,0.28)" : "transparent"}
            borderRadius="sm"
          >
            {seg.text}
          </Text>
        );
      });
    });
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toLowerCase();
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    if (isSameDay(date, now)) return "Today";
    if (isYesterday(date)) return "Yesterday";
    return date.toLocaleDateString([], {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getInitials = (name: string) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  };

  const flattenedChannels = projectList
    .flatMap(p => p.channels.map(ch => ({ ...ch, projectId: p.id })))
    .filter(ch => ch.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const timeA = a.lastMessage?.time || 0;
      const timeB = b.lastMessage?.time || 0;
      return timeB - timeA;
    });

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : "Unknown User";
  };

  return (
    <Flex h="calc(100vh - 50px)" bg={cardBg} borderRadius="0" overflow="hidden" boxShadow="none" border="1px solid" borderColor={borderColor}>
      {/* Channels Sidebar */}
      <Box
        w={{ base: "full", md: isSidebarVisible ? "350px" : "0px" }}
        bg={sidebarBg}
        borderRight={isSidebarVisible ? "1px solid" : "none"}
        borderColor={borderColor}
        display={{ base: selectedChannel ? "none" : "block", md: isSidebarVisible ? "block" : "none" }}
        transition="all 0.3s ease"
        overflow="hidden"
      >
        <VStack h="full" spacing={0} align="stretch">
          <Box p={4}>
            <Flex align="center" justify="space-between" mb={4}>
              <Text fontSize="xl" fontWeight="bold" color={textColor}>Team Chats</Text>
              {selectedChannel && (
                <IconButton
                  aria-label="Close Sidebar"
                  icon={<ChakraIcon as={FiChevronLeft} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsSidebarVisible(false)}
                  display={{ base: "none", md: "flex" }}
                />
              )}
            </Flex>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <ChakraIcon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search projects or channels..."
                borderRadius="full"
                bg={searchInputBg}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Box>

          <Box flex={1} overflowY="auto" sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: '4px' }
          }}>
            {isLoadingChannels ? (
              <Flex h="100px" align="center" justify="center" py={10}>
                <Spinner color="zs.accent" />
              </Flex>
            ) : (
              flattenedChannels.map((ch) => (
                <Flex
                  key={ch.id}
                  p={3}
                  align="center"
                  cursor="pointer"
                  transition="all 0.2s"
                  bg={selectedChannel?.channel.id === ch.id ? activeChatBg : "transparent"}
                  _hover={{ bg: activeChatBg }}
                  onClick={() => setSelectedChannel({ projectId: ch.projectId, channel: ch })}
                  position="relative"
                >
                  <Flex
                    w="32px" h="32px"
                    borderRadius="full"
                    bg="transparent"
                    border="1px solid"
                    borderColor="zs.accent"
                    color="zs.accent"
                    mr={3}
                    align="center"
                    justify="center"
                  >
                    <ChakraIcon as={FiUsers} boxSize="16px" />
                  </Flex>
                  <VStack align="start" spacing={0} flex={1} overflow="hidden">
                    <HStack w="full" justify="space-between" align="baseline">
                      <Text fontWeight="semibold" fontSize="sm" color={textColor} isTruncated flex={1}>{ch.name}</Text>
                      {ch.lastMessage && (
                        <Text fontSize="10px" color={mutedTextColor} ml={2} whiteSpace="nowrap">
                          {formatLastMessageTime(ch.lastMessage.time)}
                        </Text>
                      )}
                    </HStack>
                    <HStack spacing="3px" w="full">
                      {ch.lastMessage && ch.lastMessage.userId === currentUser.id && (
                        <ChatRowTicks
                          basePath={`organization/${organizationId}/projects/${ch.projectId}/channels/${ch.id}`}
                          myUid={currentUser.id}
                          createdAt={ch.lastMessage.time}
                        />
                      )}
                      <Text fontSize="xs" color={mutedTextColor} isTruncated flex={1}>
                        {ch.lastMessage ? ch.lastMessage.text : "No messages yet"}
                      </Text>
                    </HStack>
                  </VStack>
                  {ch.unreadCount > 0 && (
                    <Badge colorScheme="red" borderRadius="full" px={1.5} minW="18px" h="18px" display="flex" alignItems="center" justifyContent="center" variant="solid" fontSize="10px" ml={2}>
                      {ch.unreadCount}
                    </Badge>
                  )}
                </Flex>
              ))
            )}
          </Box>
        </VStack>
      </Box>

      {/* Main Chat Area */}
      <Flex flex={1} direction="column" display={{ base: selectedChannel ? "flex" : "none", md: "flex" }} bg={chatBg}>
        {selectedChannel ? (
          <>
            {/* Header */}
            <Flex p={3} align="center" justify="space-between" bg={cardBg} borderBottom="1px solid" borderColor={borderColor} boxShadow="sm" zIndex={1}>
              <HStack spacing={3} flex={1}>
                {!isSidebarVisible && (
                  <IconButton
                    aria-label="Open Sidebar"
                    icon={<ChakraIcon as={FiChevronRight} />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsSidebarVisible(true)}
                    display={{ base: "none", md: "flex" }}
                  />
                )}
                <IconButton
                  display={{ base: "flex", md: "none" }}
                  aria-label="Back"
                  icon={<ChakraIcon as={FiChevronLeft} />}
                  variant="ghost"
                  onClick={() => setSelectedChannel(null)}
                />
                <Flex
                  w="32px" h="32px"
                  borderRadius="full"
                  bg="transparent"
                  border="1px solid"
                  borderColor="zs.accent"
                  color="zs.accent"
                  align="center"
                  justify="center"
                >
                  <ChakraIcon as={FiUsers} boxSize="16px" />
                </Flex>
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold" fontSize="md" color={textColor}>{selectedChannel.channel.name}</Text>
                  <Text fontSize="xs" color={mutedTextColor}>Team Members</Text>
                </VStack>
              </HStack>
              <HStack ml={2} spacing={1}>
                {isMessageSearchOpen && (
                  <InputGroup size="sm" w="200px">
                    <Input
                      placeholder="Search..."
                      borderRadius="full"
                      bg={searchInputBg}
                      value={messageSearchText}
                      onChange={(e) => setMessageSearchText(e.target.value)}
                      autoFocus
                      pr="2rem"
                    />
                    <InputRightElement width="2rem">
                      <IconButton
                        aria-label="Close search"
                        icon={<ChakraIcon as={FiChevronLeft} />}
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setIsMessageSearchOpen(false);
                          setMessageSearchText("");
                        }}
                      />
                    </InputRightElement>
                  </InputGroup>
                )}
                <IconButton
                  aria-label="Search"
                  icon={<ChakraIcon as={FiSearch} />}
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMessageSearchOpen(!isMessageSearchOpen)}
                  color={isMessageSearchOpen ? "zs.accent" : "inherit"}
                />
                <IconButton
                  aria-label="Info"
                  icon={<ChakraIcon as={FiInfo} />}
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsInfoPanelOpen(!isInfoPanelOpen)}
                  color={isInfoPanelOpen ? "zs.accent" : "inherit"}
                />
              </HStack>
            </Flex>

            {/* Messages List Wrapper */}
            <Box flex={1} position="relative" display="flex" flexDirection="column" minH={0}>
              {/* Messages List */}
              <Box flex={1} p={4} overflowY="auto" ref={scrollRef} onScroll={handleScroll} sx={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
              }}>
                {isLoadingMessages ? (
                <Flex h="full" align="center" justify="center"><Spinner color="zs.accent" /></Flex>
              ) : (
                <>
                  {hasMoreMessages && (
                    <Flex justify="center" mb={4}>
                      <Button size="xs" variant="ghost" onClick={loadMoreMessages} colorScheme="blue">
                        Load Earlier Messages
                      </Button>
                    </Flex>
                  )}
                  {displayMessages
                    .filter(msg => !messageSearchText || msg.text?.toLowerCase().includes(messageSearchText.toLowerCase()))
                    .map((msg, index, filteredArr) => {
                      const isMe = msg.userId === currentUser.id;
                      const showDate = index === 0 || !isSameDay(new Date(msg.createdAt), new Date(filteredArr[index - 1].createdAt));

                      return (
                        <React.Fragment key={msg.id}>
                          {showDate && (
                            <Flex justify="center" my={4}>
                              <Badge bg={dateBadgeBg} px={3} py={1} borderRadius="md" color={mutedTextColor} shadow="sm">
                                {formatDate(msg.createdAt)}
                              </Badge>
                            </Flex>
                          )}
                          <Flex justify={isMe ? "flex-end" : "flex-start"} mb={2} px={{ base: 2, md: 4 }}>
                            <VStack align={isMe ? "flex-end" : "flex-start"} spacing={1} maxW="70%">
                              {!isMe && <Text fontSize="10px" color={mutedTextColor} ml={1}>{getUserName(msg.userId)}</Text>}
                              <Box
                                bg={isMe ? messageMeBg : messageOtherBg}
                                color={isMe ? messageMeTextColor : textColor}
                                px={3}
                                py={2}
                                borderRadius="lg"
                                borderTopRightRadius={isMe ? 0 : "lg"}
                                borderTopLeftRadius={isMe ? "lg" : 0}
                                boxShadow="sm"
                                position="relative"
                                border="1px solid"
                                borderColor={isMe ? "transparent" : borderColor}
                                // A received message that @-mentions me: a gold edge rather
                                // than a fill, so it reads as a marker without competing
                                // with the bubble's own background.
                                borderLeftWidth={!isMe && mentionsUser(msg.mentions, currentUser.id) ? "3px" : undefined}
                                borderLeftColor={!isMe && mentionsUser(msg.mentions, currentUser.id) ? "#B59143" : undefined}
                                cursor={["image", "video", "document", "audio"].includes(msg.type) ? "pointer" : "default"}
                                onClick={() => {
                                  if (["image", "video", "document", "audio"].includes(msg.type)) {
                                    setViewerMedia({ url: (msg.fileUrl || msg.url || msg.text)!, type: msg.type, name: msg.fileName || "Audio" });
                                  }
                                }}
                              >
                                {msg.type === "text" && <Text fontSize="sm" whiteSpace="pre-wrap">{renderMessageText(msg.text!, msg.mentions)}</Text>}
                                {msg.type === "task" && (
                                  <HStack spacing={2} pb={1}>
                                    <ChakraIcon
                                      as={msg.subTaskId ? FiCheckCircle : FiLayers}
                                      color="zs.accent"
                                    />
                                    <Text fontSize="sm" fontWeight="medium">{msg.taskName}</Text>
                                  </HStack>
                                )}
                                {msg.type === "image" && (
                                  <Box borderRadius="md" overflow="hidden">
                                    <ResolvedImage
                                      urlOrPath={(msg.fileUrl || msg.url || msg.text)!}
                                      alt={msg.fileName}
                                      style={{ maxWidth: '100%', maxHeight: '300px', display: 'block' }}
                                    />
                                    {msg.caption && <Text fontSize="xs" mt={1}>{msg.caption}</Text>}
                                  </Box>
                                )}
                                {msg.type === "video" && (
                                  <Box borderRadius="md" overflow="hidden" position="relative">
                                    <ResolvedVideo urlOrPath={(msg.fileUrl || msg.url || msg.text)!} style={{ maxWidth: '100%', maxHeight: '300px' }} />
                                    <Flex justify="center" position="absolute" top="0" left="0" w="full" h="full" align="center" bg="blackAlpha.300">
                                      <ChakraIcon as={FiMaximize2} color="white" boxSize={8} />
                                    </Flex>
                                    {msg.caption && <Text fontSize="xs" mt={1}>{msg.caption}</Text>}
                                  </Box>
                                )}
                                {msg.type === "audio" && (
                                  <Box borderRadius="md" py={2} w="280px" maxW="100%">
                                    <ResolvedAudio
                                      urlOrPath={(msg.fileUrl || msg.url || msg.text)!}
                                      controls
                                      style={{ width: '100%' }}
                                    />
                                    {msg.caption && <Text fontSize="xs" mt={1}>{msg.caption}</Text>}
                                  </Box>
                                )}
                                {msg.type === "document" && (
                                  <HStack bg="blackAlpha.100" p={2} borderRadius="md">
                                    <ChakraIcon as={FiFileText} />
                                    <Text fontSize="xs" isTruncated maxW="150px">{msg.fileName}</Text>
                                    <ChakraIcon as={FiDownload} ml="auto" />
                                  </HStack>
                                )}
                                <MessageMeta
                                  timeLabel={formatTime(msg.createdAt)}
                                  state={isMe ? tickStateFor(msg) : null}
                                  tint={isMe ? messageMeTextColor : mutedTextColor}
                                  onRetry={() => outbox.retry(msg.id)}
                                  onDiscard={() => outbox.discard(msg.id)}
                                />
                              </Box>
                            </VStack>
                          </Flex>
                        </React.Fragment>
                      );
                    })
                  }
                </>
              )}
            </Box>

            {showScrollButton && (
              <IconButton
                aria-label="Scroll to bottom"
                icon={<ChakraIcon as={FiChevronsDown} />}
                position="absolute"
                bottom="20px"
                right="20px"
                zIndex={20}
                bg={scrollBtnBg}
                color={scrollBtnColor}
                _hover={{ bg: scrollBtnHoverBg }}
                borderRadius="full"
                size="md"
                boxShadow="lg"
                onClick={() => scrollToBottom("smooth")}
              />
            )}
          </Box>

            {/* Input Area */}
            <Box p={3} bg={cardBg} borderTop="1px solid" borderColor={borderColor}>
              <HStack spacing={2} align="flex-end">
                <Menu>
                  <MenuButton as={IconButton} aria-label="Attachments" icon={<ChakraIcon as={FiPaperclip} />} variant="ghost" borderRadius="full" />
                  <MenuList>
                    <MenuItem icon={<ChakraIcon as={FiImage} />} onClick={() => document.getElementById('image-upload')?.click()}>Image</MenuItem>
                    <MenuItem icon={<ChakraIcon as={FiVideo} />} onClick={() => document.getElementById('video-upload')?.click()}>Video</MenuItem>
                    <MenuItem icon={<ChakraIcon as={FiFileText} />} onClick={() => document.getElementById('file-upload')?.click()}>Document</MenuItem>
                    {hasTaskPermission && (
                      <>
                        <Divider />
                        <MenuItem
                          icon={<ChakraIcon as={FiLayers} />}
                          onClick={() => setIsTaskGroupModalOpen(true)}
                        >
                          Task Group
                        </MenuItem>
                        <MenuItem
                          icon={<ChakraIcon as={FiCheckCircle} />}
                          onClick={() => {
                            fetchTaskGroups();
                            setIsTaskSelectModalOpen(true);
                          }}
                        >
                          Task
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </Menu>

                {/* Hidden File Inputs */}
                <input type="file" id="image-upload" hidden accept="image/*" onChange={(e) => handleFileUpload(e, "image")} />
                <input type="file" id="video-upload" hidden accept="video/*" onChange={(e) => handleFileUpload(e, "video")} />
                <input type="file" id="file-upload" hidden onChange={(e) => handleFileUpload(e, "document")} />

                <Box flex={1} position="relative">
                  {isUploading && (
                    <Box position="absolute" top="-4px" left={0} w={`${uploadProgress}%`} h="2px" bg="zs.accent" transition="width 0.2s" />
                  )}
                  <MentionPicker candidates={mentionMatches} onPick={handlePickMention} />
                  <Input
                    ref={messageInputRef}
                    placeholder="Type a message... use @ to mention"
                    value={inputText}
                    onChange={handleMessageInputChange}
                    onClick={handleMessageInputCaretMove}
                    onKeyUp={handleMessageInputCaretMove}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && mentionQuery) setMentionQuery(null);
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    variant="filled"
                    bg={inputBg}
                    _hover={{ bg: inputHoverBg }}
                    _focus={{ bg: inputFocusBg, borderColor: "zs.accent" }}
                    borderRadius="xl"
                  />
                </Box>

                {inputText.trim() ? (
                  <IconButton
                    aria-label="Send"
                    icon={<ChakraIcon as={FiSend} />}
                    colorScheme="blue"
                    bg="zs.accent"
                    _hover={{ bg: "zs.accentLight" }}
                    borderRadius="full"
                    onClick={sendMessage}
                  />
                ) : (
                  <IconButton aria-label="Voice" icon={<ChakraIcon as={FiMic} />} variant="ghost" borderRadius="full" />
                )}
              </HStack>
            </Box>
          </>
        ) : (
          <Flex h="full" align="center" justify="center" direction="column">
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>ZenSight Team Chats</Text>
            <Text color={mutedTextColor}>Select a channel to start messaging with your team</Text>
          </Flex>
        )}
      </Flex>
      {/* Info Panel */}
      {isInfoPanelOpen && selectedChannel && (
        <Box
          w="300px"
          bg={cardBg}
          borderLeft="1px solid"
          borderColor={borderColor}
          display={{ base: "none", lg: "block" }}
          overflowY="auto"
        >
          <VStack p={4} spacing={6} align="stretch">
            <Flex justify="space-between" align="center">
              <Text fontWeight="bold">Channel Info</Text>
              <IconButton
                aria-label="Close"
                icon={<ChakraIcon as={FiX} />}
                size="sm"
                variant="ghost"
                onClick={() => setIsInfoPanelOpen(false)}
              />
            </Flex>

            <VStack align="center" spacing={3} py={4}>
              <VStack spacing={0}>
                <Text fontWeight="bold" fontSize="lg">{selectedChannel.channel.name}</Text>
                <Text fontSize="sm" color={mutedTextColor}>Project: {projectList.find(p => p.id === selectedChannel.projectId)?.name}</Text>
              </VStack>
            </VStack>

            <Divider />

            <Box>
              <HStack mb={3} justify="space-between">
                <Text fontWeight="bold" fontSize="sm">Members</Text>
                <Badge borderRadius="full" px={2}>{users.filter(u => u.assignedChannels?.[selectedChannel.projectId]?.includes(selectedChannel.channel.id) || isAdminMember(u, orgAdminId)).length}</Badge>
              </HStack>
              <VStack align="stretch" spacing={3}>
                {users
                  .filter(u => u.assignedChannels?.[selectedChannel.projectId]?.includes(selectedChannel.channel.id) || isAdminMember(u, orgAdminId))
                  .map(u => (
                    <HStack key={u.id} spacing={3}>
                      <Flex
                        w="32px" h="32px"
                        borderRadius="full"
                        bg="transparent"
                        border="1px solid"
                        borderColor="zs.accent"
                        color="zs.accent"
                        align="center"
                        justify="center"
                        fontSize="xs"
                        fontWeight="bold"
                      >
                        {getInitials(`${u.firstName} ${u.lastName}`)}
                      </Flex>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" fontWeight="semibold">{u.firstName} {u.lastName}</Text>
                        <Text fontSize="10px" color={mutedTextColor}>{u.role || u.permissions}</Text>
                      </VStack>
                    </HStack>
                  ))}
              </VStack>
            </Box>
          </VStack>
        </Box>
      )}

      {/* Task Group Modal */}
      <Modal isOpen={isTaskGroupModalOpen} onClose={() => setIsTaskGroupModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalBody p={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="bold">Create Task Group</Text>
              <Input
                placeholder="Task Group Title"
                value={taskGroupTitle}
                onChange={(e) => setTaskGroupTitle(e.target.value)}
              />
              <Input
                placeholder="Description (Optional)"
                value={taskGroupDesc}
                onChange={(e) => setTaskGroupDesc(e.target.value)}
              />
              <HStack justify="flex-end">
                <Button variant="ghost" onClick={() => setIsTaskGroupModalOpen(false)}>Cancel</Button>
                <Button
                  bg="zs.accent"
                  color="white"
                  _hover={{ bg: "zs.accentLight" }}
                  isLoading={isSavingTask}
                  onClick={handleCreateTaskGroup}
                >
                  Create
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Task Select Modal */}
      <Modal isOpen={isTaskSelectModalOpen} onClose={() => setIsTaskSelectModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalBody p={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="bold">Select Task Group</Text>
              <Box maxH="300px" overflowY="auto">
                {taskGroupList.map(group => (
                  <Box
                    key={group.id}
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: activeChatBg }}
                    borderRadius="md"
                    onClick={() => {
                      setSelectedTaskGroup(group);
                      setIsTaskSelectModalOpen(false);
                      fetchChannelMembers();
                      setIsSubTaskModalOpen(true);
                    }}
                  >
                    <Text fontWeight="medium">{group.title}</Text>
                  </Box>
                ))}
              </Box>
              <Button variant="ghost" onClick={() => setIsTaskSelectModalOpen(false)}>Cancel</Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Sub-Task Detail Modal */}
      <Modal isOpen={isSubTaskModalOpen} onClose={() => setIsSubTaskModalOpen(false)} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalBody p={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="bold">Create Task in {selectedTaskGroup?.title}</Text>
              <VStack align="stretch" spacing={3}>
                <Box>
                  <Text fontSize="xs" color={mutedTextColor} mb={1}>Task Name</Text>
                  <Input
                    placeholder="e.g. Design UI Mockups"
                    value={subTaskName}
                    onChange={(e) => setSubTaskName(e.target.value)}
                  />
                </Box>
                <Flex gap={4}>
                  <Box flex={1}>
                    <Text fontSize="xs" color={mutedTextColor} mb={1}>Planned Start</Text>
                    <Input type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} />
                  </Box>
                  <Box flex={1}>
                    <Text fontSize="xs" color={mutedTextColor} mb={1}>Planned End</Text>
                    <Input type="date" value={plannedEnd} onChange={(e) => setPlannedEnd(e.target.value)} />
                  </Box>
                </Flex>
                <Flex gap={4}>
                  <Box flex={1}>
                    <Text fontSize="xs" color={mutedTextColor} mb={1}>Assign Members</Text>
                    <Menu closeOnSelect={false}>
                      <MenuButton
                        as={Button}
                        size="sm"
                        w="full"
                        variant="outline"
                        rightIcon={<ChakraIcon as={FiChevronDown} />}
                        textAlign="left"
                        fontWeight="normal"
                      >
                        {assignedSubTaskUsers.length === 0
                          ? "Select Members"
                          : `${assignedSubTaskUsers.length} Selected`}
                      </MenuButton>
                      <MenuList maxHeight="200px" overflowY="auto">
                        {channelMembers.map(member => (
                          <MenuItem
                            key={member.id}
                            onClick={() => {
                              setAssignedSubTaskUsers(prev =>
                                prev.includes(member.id)
                                  ? prev.filter(id => id !== member.id)
                                  : [...prev, member.id]
                              );
                            }}
                          >
                            <HStack flex={1}>
                              <ChakraIcon
                                as={assignedSubTaskUsers.includes(member.id) ? FiCheckCircle : FiLayers}
                                color={assignedSubTaskUsers.includes(member.id) ? "zs.accent" : "inherit"}
                              />
                              <Text fontSize="sm">{member.name}</Text>
                              <Spacer />
                              <Text fontSize="10px" color={mutedTextColor}>{member.role}</Text>
                            </HStack>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </Box>
                  <Box flex={1} /> {/* Empty space for half-width alignment */}
                </Flex>
              </VStack>
              <HStack justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={() => setIsSubTaskModalOpen(false)}>Cancel</Button>
                <Button
                  bg="zs.accent"
                  color="white"
                  _hover={{ bg: "zs.accentLight" }}
                  isLoading={isSavingTask}
                  onClick={handleCreateSubTask}
                >
                  Save Task
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Media Viewer Modal */}
      <Modal isOpen={!!viewerMedia} onClose={() => setViewerMedia(null)} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(10px)" />
        <ModalContent bg="transparent" boxShadow="none" m={0}>
          <HStack position="absolute" top={4} right={4} zIndex={20} spacing={4}>
            <IconButton
              aria-label="Download"
              icon={<ChakraIcon as={FiDownload} />}
              onClick={() => viewerMedia && handleDownload(viewerMedia.url, viewerMedia.name)}
              colorScheme="whiteAlpha"
              color="white"
              variant="solid"
              borderRadius="full"
              _hover={{ bg: "whiteAlpha.400" }}
            />
            <IconButton
              aria-label="Close"
              icon={<ChakraIcon as={FiX} />}
              onClick={() => setViewerMedia(null)}
              colorScheme="whiteAlpha"
              color="white"
              variant="solid"
              borderRadius="full"
              _hover={{ bg: "whiteAlpha.400" }}
            />
          </HStack>
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" bg="transparent">
            <Flex direction="column" align="center" justify="center" w="full" h="100vh" p={4}>
              {viewerMedia?.type === "image" && (
                <ResolvedImage urlOrPath={viewerMedia.url} alt="View" style={{ maxHeight: "90vh", maxWidth: "90vw", borderRadius: "8px", objectFit: "contain" }} />
              )}
              {viewerMedia?.type === "video" && (
                <ResolvedVideo urlOrPath={viewerMedia.url} controls autoPlay style={{ maxHeight: "90vh", maxWidth: "90vw", borderRadius: "8px" }} />
              )}
              {viewerMedia?.type === "audio" && (
                <Flex direction="column" align="center" bg={cardBg} p={10} borderRadius="lg" shadow="2xl" w="350px" maxW="90%">
                  <ChakraIcon as={FiMic} boxSize={20} color="zs.accent" mb={6} />
                  <Text fontWeight="bold" mb={6} textAlign="center">{viewerMedia.name}</Text>
                  <ResolvedAudio urlOrPath={viewerMedia.url} controls style={{ width: "100%" }} />
                  <Button
                    mt={6}
                    onClick={() => viewerMedia && handleDownload(viewerMedia.url, viewerMedia.name)}
                    leftIcon={<ChakraIcon as={FiDownload} />}
                    colorScheme="blue"
                    bg="zs.accent"
                    _hover={{ bg: "zs.accentLight" }}
                  >
                    Download Audio
                  </Button>
                </Flex>
              )}
              {viewerMedia?.type === "document" && (
                <Flex direction="column" align="center" bg={cardBg} p={10} borderRadius="lg" shadow="2xl" maxW="400px">
                  <ChakraIcon as={FiFileText} boxSize={20} color="zs.accent" mb={6} />
                  <Text fontWeight="bold" mb={6} textAlign="center">{viewerMedia.name}</Text>
                  <Button
                    onClick={() => viewerMedia && handleDownload(viewerMedia.url, viewerMedia.name)}
                    leftIcon={<ChakraIcon as={FiDownload} />}
                    colorScheme="blue"
                    bg="zs.accent"
                    _hover={{ bg: "zs.accentLight" }}
                  >
                    Download File
                  </Button>
                </Flex>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default TeamChatsMain;

